<template>
    <v-card class="elevation-3 mx-auto my-4">
      <!-- Header / Toolbar -->
      <!-- <v-toolbar flat color="primary" dark>
        <v-icon left>mdi-wrench</v-icon>
        <v-toolbar-title class="font-weight-bold">
          Service Card: {{ selectedAutoService ? selectedAutoService.card_number : 'N/A' }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
  
        
        <template v-if="!isEditing && selectedAutoService">
          <v-chip
            :color="getStatusColor(selectedAutoService.status)"
            text-color="white"
            small
            class="mr-2 font-weight-bold text-uppercase"
          >
            {{ selectedAutoService.status || 'Pending' }}
          </v-chip>
          <v-chip
            :color="getPriorityColor(selectedAutoService.priority)"
            outlined
            dark
            small
            class="mr-2 font-weight-bold text-uppercase"
          >
            {{ selectedAutoService.priority || 'Normal' }}
          </v-chip>
        </template>
  
        
        <template v-if="!isEditing">
          <v-btn icon color="white" @click="enableEdit" title="Edit Service Card">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon color="error lighten-2" @click="confirmDeleteDialog = true" title="Delete Service Card">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
        <template v-else>
          <v-btn color="success" class="mr-2" :loading="saving" @click="saveChanges">
            <v-icon left>mdi-content-save</v-icon> Save
          </v-btn>
          <v-btn color="grey lighten-1" text @click="cancelEdit">
            Cancel
          </v-btn>
        </template>
      </v-toolbar> -->
  
      <v-card-text class="pt-4">
        <v-form ref="form" v-model="isFormValid" lazy-validation>
          <!-- Section 1: Job Identifiers -->
          <v-subheader class="font-weight-bold pl-0 text-subtitle-1 primary--text">
            <v-icon left color="primary">mdi-card-account-details-outline</v-icon> General Overview
          </v-subheader>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.card_number"
                label="Card Number"
                outlined
                dense
                disabled
                prepend-inner-icon="mdi-pound"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.client_reference"
                label="Client Reference"
                outlined
                dense
                disabled
                prepend-inner-icon="mdi-account"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.invoice_number"
                label="Invoice Number"
                outlined
                dense
                disabled
                prepend-inner-icon="mdi-file-document-outline"
              />
            </v-col>
          </v-row>
  
          <v-divider class="my-3"></v-divider>
  
          <!-- Section 2: Vehicle Specs & Odometer -->
          <v-subheader class="font-weight-bold pl-0 text-subtitle-1 primary--text">
            <v-icon left color="primary">mdi-car</v-icon> Vehicle Information
          </v-subheader>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.license_plate"
                label="License Plate"
                outlined
                dense
                @change="updateSelectedServiceFields('license_plate')"
                prepend-inner-icon="mdi-numeric"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.vin"
                label="VIN"
                outlined
                dense
                @change="updateSelectedServiceFields('vin')"
                prepend-inner-icon="mdi-barcode"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.make"
                label="Make"
                outlined
                dense
                @change="updateSelectedServiceFields('make')"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.model"
                label="Model"
                outlined
                dense
                @change="updateSelectedServiceFields('model')"
              />
            </v-col>
            <v-col cols="12" sm="2">
              <v-text-field
                v-model="form.year"
                label="Year"
                type="number"
                outlined
                dense
                @change="updateSelectedServiceFields('year')"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="form.style"
                label="Style or Detailed Description"
                placeholder="2.5 litre engine 6 cylinder w/Navigation"
                outlined
                dense
                @change="updateSelectedServiceFields('style')"
              />
            </v-col>
            <v-col cols="12" sm="2">
              <v-text-field
                v-model="form.color"
                label="Color"
                outlined
                dense
                @change="updateSelectedServiceFields('color')"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.fuel_level"
                label="Fuel Level"
                outlined
                dense
                @change="updateSelectedServiceFields('fuel_level')"
                prepend-inner-icon="mdi-gas-station"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.mileage_in"
                label="Mileage In"
                outlined
                dense
                @change="updateSelectedServiceFields('mileage_in')"
                prepend-inner-icon="mdi-speedometer"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.mileage_out"
                label="Mileage Out"
                outlined
                dense
                @change="updateSelectedServiceFields('mileage_out')"
                prepend-inner-icon="mdi-speedometer"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-combobox
                v-model="form.obd_ii_error_codes"
                label="DTC / OBD Error Codes"
                placeholder="Type code and press Enter (e.g. P0300)"
                outlined
                dense
                multiple
                chips
                small-chips
                deletable-chips
                prepend-inner-icon="mdi-alert-outline"
                @input="updateOBDErrorCodes('obd_ii_error_codes')"
              ></v-combobox>
            </v-col>
          </v-row>
  
          <v-divider class="my-3"></v-divider>
  
          <!-- Section 3: Service Schedule & Assignment -->
          <v-subheader class="font-weight-bold pl-0 text-subtitle-1 primary--text">
            <v-icon left color="primary">mdi-clock-outline</v-icon> Schedule & Assignment
          </v-subheader>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-select
                v-model="form.status"
                :items="statusOptions"
                label="Status"
                outlined
                dense
                @change="updateSelectedServiceFields('status')"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-select
                v-model="form.priority"
                :items="priorityOptions"
                label="Priority"
                outlined
                dense
                @change="updateSelectedServiceFields('priority')"
              />
            </v-col>
            <!-- <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.assigned_technician_id"
                label="Technician ID"
                type="number"
                outlined
                dense
                @change="updateSelectedServiceFields('assigned_technician_id')"
                prepend-inner-icon="mdi-account-wrench"
              />
            </v-col> -->
  
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.date_vehicle_admitted"
                label="Admitted Date"
                outlined
                dense
                @change="updateSelectedServiceFields('date_vehicle_admitted')"
                prepend-inner-icon="mdi-calendar-import"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.estimated_completion_at"
                label="Estimated Completion"
                outlined
                dense
                @change="updateSelectedServiceFields('estimated_completion_at')"
                prepend-inner-icon="mdi-calendar-clock"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.completed_at"
                label="Completed Date"
                outlined
                dense
                @change="updateSelectedServiceFields('completed_at')"
                prepend-inner-icon="mdi-calendar-check"
              />
            </v-col>
          </v-row>
  
          <v-divider class="my-3"></v-divider>
  
          <!-- Section 4: Work Details & Notes -->
          <v-subheader class="font-weight-bold pl-0 text-subtitle-1 primary--text">
            <v-icon left color="primary">mdi-clipboard-text-outline</v-icon> Issues & Service Details
          </v-subheader>
          <v-row dense>
            <v-col cols="12">
              <v-textarea
                v-model="form.customer_reported_issues"
                label="Customer Reported Issues *"
                outlined
                rows="3"
                
                @change="updateSelectedServiceFields('customer_reported_issues')"
              />
            </v-col>
           
            <v-col cols="12">
              <v-textarea
                v-model="form.work_performed"
                label="Work Performed"
                outlined
                rows="3"
                @change="updateSelectedServiceFields('work_performed')"
              />
            </v-col>
            <!-- <v-col cols="12">
              <v-text-field
                v-model="form.case_docs_folder"
                label="Case Docs Folder Path"
                outlined
                dense
                @change="updateSelectedServiceFields('column')"
                prepend-inner-icon="mdi-folder-outline"
              />
            </v-col> -->
          </v-row>
        </v-form>
      </v-card-text>
  
      <!-- Delete Confirmation Modal -->
      <v-dialog v-model="confirmDeleteDialog" max-width="400">
        <v-card>
          <v-card-title class="text-h6 error--text">
            <v-icon left color="error">mdi-alert-circle</v-icon> Delete Service Card?
          </v-card-title>
          <v-card-text>
            Are you sure you want to delete service card
            <strong>{{ selectedAutoService ? selectedAutoService.card_number : '' }}</strong>? This action cannot be undone.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="confirmDeleteDialog = false">Cancel</v-btn>
            <v-btn color="error" :loading="deleting" @click="handleDelete">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </template>
  
  <script>
  import axios from "@/plugins/axios";
import helpersJs from "@/plugins/helpers"

  export default {
    name: 'AutoServiceCardDetail',
    props: {
      selectedAutoService: {
        type: Object,
        default: null,
      },
    },
    computed:{
      getAccessToken(){
        return this.$store.getters.getAccessToken
      }
    },
    data() {
      return {
        serviceTable: "auto_service_cards",
        isEditing: false,
        isFormValid: true,
        saving: false,
        deleting: false,
        confirmDeleteDialog: false,
        statusOptions: [
          'pending',
          'inspection',
          'in_progress',
          'awaiting_parts',
          'completed',
          'cancelled',
        ],
        priorityOptions: ['low', 'normal', 'high', 'urgent'],
        form: this.getEmptyForm(),
      };
    },
    watch: {
      selectedAutoService: {
        immediate: true,
        handler(newVal) {
          if (newVal) {
            let codes = newVal.obd_ii_error_codes;

            // Handle JSON string from API if not automatically cast by frontend/backend
            if (typeof codes === 'string') {
              try {
                codes = JSON.parse(codes);
              } catch (e) {
                codes = [];
              }
            }

            this.form = {
              ...newVal,
              obd_ii_error_codes: Array.isArray(codes) ? codes : []
            };
          } else {
            this.form = this.getEmptyForm();
          }
        },
      }
    },
    methods: {
      getEmptyForm() {
        return {
          id: null,
          card_number: '',
          client_reference: '',
          invoice_number: '',
          vin: '',
          license_plate: '',
          make: '',
          model: '',
          year: null,
          color: '',
          style: '',
          mileage_in: '',
          mileage_out: '',
          fuel_level: '',
          date_vehicle_admitted: null,
          estimated_completion_at: null,
          completed_at: null,
          customer_reported_issues: '',
          work_performed: '',
          assigned_technician_id: null,
          status: 'pending',
          priority: 'normal',
          case_docs_folder: '',
        };
      },
      enableEdit() {
        this.isEditing = true;
      },
      cancelEdit() {
        this.isEditing = false;
        if (this.selectedAutoService) {
          this.form = { ...this.selectedAutoService };
        }
        this.$emit('cancel');
      },
      async saveChanges() {
        if (!this.$refs.form.validate()) return;
  
        this.saving = true;
        try {
          // Emit updated payload back to parent component/API dispatcher
          this.$emit('update', { ...this.form });
          this.isEditing = false;
        } finally {
          this.saving = false;
        }
      },
      async handleDelete() {
        this.deleting = true;
        try {
          this.$emit('delete', this.form.id);
          this.confirmDeleteDialog = false;
        } finally {
          this.deleting = false;
        }
      },
      getStatusColor(status) {
        switch ((status || '').toLowerCase()) {
          case 'completed': return 'success';
          case 'in_progress': return 'info';
          case 'awaiting_parts': return 'warning';
          case 'cancelled': return 'error';
          default: return 'grey';
        }
      },
      getPriorityColor(priority) {
        switch ((priority || '').toLowerCase()) {
          case 'urgent': return 'red';
          case 'high': return 'orange';
          case 'normal': return 'blue';
          default: return 'grey';
        }
      },
      async updateOBDErrorCodes(column){
        
        const codes = await axios.postDataWithToken("/auth/update-obd-ii-error-codes",{
          id: this.form.id,
          obd_ii_error_codes: this.form.obd_ii_error_codes
        },
        this.getAccessToken)
        this.$toast.success("Data updated successfully!",{duration: 10000})
        this.form.obd_ii_error_codes = codes;
      },
      //done
      async updateSelectedServiceFields(columnName){
        if(!this.form.id){
          return;
        }
        await this.updateModelSingleRow(this.serviceTable, columnName, this.form.id, this.form[columnName])
      },
        //done
        async updateModelSingleRow(table, column, id, value){
        this.$store.commit("setLoader",true)
        await helpersJs.updateModelSingleRow(table, column, id, value)
        this.$store.commit("setLoader",false)
        this.$toast.success("Data updated successfully!",{duration:5000})
        this.$emit('save', this.form);
      },
      
    },
  };
  </script>
  
  <style scoped>
  .v-subheader {
    height: 36px;
  }
  </style>
